// THE PROJECT PAGE, PART BY PART (Karan's "Track Mail - Project Detail" design,
// 07/10/2026). Pure builders: data in, escaped HTML out — no DOM, no network —
// so the stage logic is tested in node (test/project-detail-parts.test.mjs)
// and project-detail.js only wires them.
//
// Wording: "project", never "job" (Karan 06/10). House numbering for document
// control (PROJECT-FOLDER-DOCUMENT_REV), never the design's ASP-XX-XX sample.

import { escapeHtml } from '../ui.js';
import {
  severityMark, locationBadge, tierOf, tierText, sortedFlags, worstSeverity, verdictText, headlineText, checkedLine,
  fmtDate as scDate, sourceChip, failedSources, flagSource, diffText, triggerPhrase, shortFlagName,
  SOURCE_ORDER, SOURCE_LABEL, SCREENING_NOTE,
} from './site-checks-ui.js';

const esc = (v) => escapeHtml(String(v == null ? '' : v));
const pad2 = (n) => String(n).padStart(2, '0');
const DONE_STATES = ['Issued', 'Complete'];
export const TONE = {
  done: 'var(--anchor)', attn: 'var(--brand-orange)', todo: 'var(--hair-strong)', ok: 'var(--status-ok-ui)', link: 'var(--link)', grey: 'var(--ink-3)',
};

const day = (v) => {
  const d = v instanceof Date ? v : new Date(v);
  return isNaN(d) ? '' : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};
const hm = (mins) => `${Math.floor(mins / 60)}:${pad2(Math.round(mins % 60))}`;

// ── Instructions: one card in front, the rest stacked behind it ──────────────
/**
 * The five stages of one instruction.
 * i: the instruction; ctx: { mins, calcs: number|null (null = not read yet),
 * issued: number, overdue: bool }.
 */
export function instructionStages(i, ctx = {}) {
  const mins = Number(ctx.mins) || 0;
  const worked = mins > 0;
  const issued = Number(ctx.issued) || 0;
  const checker = i.checkedByName || i.checkerName || i.reviewerName || i.checkedBy || null;
  const calcs = ctx.calcs;
  const stages = [
    { label: 'Instructed', note: day(i.instructedAt || i.createdAt) || 'on record', tone: 'done' },
    { label: 'Worked on', note: worked ? `${hm(mins)} logged` : 'no time yet', tone: worked ? 'done' : 'todo' },
    calcs == null
      ? { label: 'Calcs', note: 'checking the folder…', tone: 'todo' }
      : calcs > 0
        ? { label: 'Calcs', note: `${calcs} file${calcs === 1 ? '' : 's'}`, tone: 'done' }
        // An empty calcs folder on work that has started, or is overdue, is the
        // gap — orange; before any work it is simply not reached.
        : { label: 'Calcs', note: 'folder empty', tone: (worked || ctx.overdue) ? 'attn' : 'todo' },
    checker ? { label: 'Checked', note: `by ${checker}`, tone: 'done' } : { label: 'Checked', note: 'no reviewer', tone: 'todo' },
    issued ? { label: 'Issued', note: `${issued} issued`, tone: 'done' } : { label: 'Issued', note: '—', tone: DONE_STATES.includes(i.status) ? 'done' : 'todo' },
  ];
  return stages;
}

/** "1 open · waiting on calcs". */
export function instructionSummary(instructions, frontStages) {
  const open = instructions.filter((i) => !DONE_STATES.includes(i.status)).length;
  const gap = (frontStages || []).find((s) => s.tone !== 'done');
  return `${open} open${gap ? ` · waiting on ${gap.label.toLowerCase()}` : ''}`;
}

const statusTone = (s) => (DONE_STATES.includes(s) ? TONE.ok : s === 'On Hold' ? TONE.attn : TONE.link);

/** The front card. ix = which instruction is in front; n = how many. */
export function instructionCardHtml(i, { stages, meta = '', ix = 0, n = 1, canEdit = false } = {}) {
  const numeral = pad2(Number(i.seq) || ix + 1);
  const layers = Math.min(Math.max(n - 1, 0), 2);
  const back = Array.from({ length: layers }, (_, k) => `<div class="pdx-stack-back" aria-hidden="true" style="--k:${k + 1};"></div>`).join('');
  return `<div class="pdx-stack" style="--layers:${layers};">${back}
    <div class="pdx-instr${n > 1 ? ' is-cycle' : ''}" ${n > 1 ? 'role="button" tabindex="0" title="Show the next instruction"' : ''} data-front="${esc(i.seq)}">
      <div class="pdx-instr-num"><span class="pdx-numeral">${esc(numeral)}</span>${n > 1 ? `<span class="pdx-counter">${ix + 1} of ${n}</span>` : ''}</div>
      <div class="pdx-instr-body">
        <div class="pdx-instr-top">
          <span class="pdx-instr-text">${esc(i.description || 'Original scope')}</span>
          <span class="pdx-instr-status" style="color:${statusTone(i.status)};"><span class="pdx-dot" style="background:${statusTone(i.status)};"></span>${esc(i.status || 'Not Started')}${canEdit ? `<button type="button" class="linkbtn instr-edit" data-seq="${esc(i.seq)}" title="Edit instruction">✎</button>` : ''}</span>
        </div>
        ${meta ? `<div class="pdx-instr-meta">${esc(meta)}</div>` : ''}
        <div class="pdx-stages">${stages.map((s) => `<div class="pdx-stage">
          <span class="pdx-stage-bar" style="background:${TONE[s.tone]};"></span>
          <span class="pdx-stage-label" style="color:${s.tone === 'todo' ? 'var(--ink-3)' : s.tone === 'attn' ? TONE.attn : 'var(--ink)'};">${esc(s.label)}</span>
          <span class="pdx-stage-note" style="color:${s.tone === 'attn' ? TONE.attn : 'var(--ink-3)'};">${esc(s.note)}</span>
        </div>`).join('')}</div>
      </div>
    </div></div>`;
}

// ── Track summary chips ──────────────────────────────────────────────────────
/** When the summary engine is unavailable: the gaps the page already knows. */
export function fallbackActions({ feeMissing = false, overdue = false, noDeadline = false, noReviewer = false, contact = '' } = {}) {
  const out = [];
  if (feeMissing) out.push({ kind: 'record-fee', label: 'Record the fee' });
  if (overdue) out.push({ kind: 'agree-date', label: contact ? `Agree a new date with ${contact}` : 'Agree a new date' });
  else if (noDeadline) out.push({ kind: 'set-deadline', label: 'Set a deadline' });
  if (noReviewer) out.push({ kind: 'assign-reviewer', label: 'Assign a reviewer' });
  return out.slice(0, 3);
}
export const chipsHtml = (actions = []) => actions.map((a) =>
  `<button type="button" class="pdx-chip" data-act="${esc(a.kind)}"${a.flagId ? ` data-flag="${esc(a.flagId)}"` : ''}><span class="pdx-dot" style="background:var(--brand-orange);"></span>${esc(a.label)}</button>`).join('');

// ── Activity ─────────────────────────────────────────────────────────────────
/**
 * The project's history, newest first, from its own records: the golden thread
 * (enquiry, instructions, invoices, notes and calls), the registers and the
 * email index, plus the one thing that is an absence — a deadline that passed
 * with nothing issued.
 */
export function activityItems({ project = {}, enquiryNo = '', nodes = [], documents = [], emails = [], dueVal = null, overdue = false, issuedCount = 0, leadName = '', siteCheck = null } = {}) {
  const out = [];
  const at = (v) => { const d = v instanceof Date ? v : new Date(v); return isNaN(d) ? null : d; };
  if (overdue && dueVal && !issuedCount) out.push({ when: at(dueVal), text: `Deadline of ${day(dueVal)} passed with nothing issued`, tone: 'attn' });
  const sc = siteActivityItem(siteCheck);
  if (sc) out.push(sc);
  for (const n of nodes) {
    if (n.withheld) { out.push({ when: at(n.at), text: `${n.eyebrow} on record`, tone: 'todo' }); continue; }
    if (n.kind === 'instruction') {
      const seq = n.record && n.record.seq != null ? pad2(n.record.seq) : '';
      const by = n.byName || (seq === '01' ? leadName : '');
      out.push({ when: at(n.at), text: by ? `${by} opened instruction ${seq}` : `Instruction ${seq} opened`, tone: 'done' });
    } else if (n.kind === 'enquiry') {
      out.push({ when: at(n.at), text: `Enquiry ${n.ref || ''} received${n.record && n.record.clientName ? ` — ${n.record.clientName}` : ''}`.replace(/\s+/g, ' '), tone: 'done' });
    } else if (n.kind === 'invoice') {
      out.push({ when: at(n.at), text: `${n.title} ${n.eyebrow === 'Payment' ? 'paid' : 'raised'}`, tone: 'done', pay: n.eyebrow === 'Payment' });
    } else {
      out.push({ when: at(n.at), text: `${n.eyebrow} — ${n.title}`, tone: 'done' });
    }
  }
  if (project.createdAt) out.push({ when: at(project.createdAt), text: enquiryNo ? `Project created from enquiry ${enquiryNo}${project.clientName ? ` — ${project.clientName}` : ''}` : 'Project created', tone: 'todo' });
  for (const r of documents) {
    const label = r.direction === 'issued' ? 'Issued' : 'Received';
    out.push({ when: at(r.date || r.createdAt), text: `${label} ${[r.docNumber, r.revision].filter(Boolean).join(' ') || r.filename || 'a document'}`, tone: r.direction === 'issued' ? 'done' : 'todo' });
  }
  for (const m of emails) {
    const who = m.direction === 'out' ? 'Sent' : `${String(m.fromName || m.from || 'Someone').replace(/<.*>/, '').replace(/"/g, '').trim()} wrote`;
    // The subject without its reply prefixes or the project tag ("RE: TRACK// 8601 …").
    const subj = String(m.subject || '').replace(/^\s*((re|fw|fwd)\s*:\s*)+/i, '').replace(/\b[A-Z]{2,}\/\/\s*(\d{3,6}\b)?\s*/g, '').trim();
    out.push({ when: at(m.date), text: `${who}: ${subj || '(no subject)'}`, tone: 'todo' });
  }
  return out.filter((x) => x.when).sort((a, b) => b.when - a.when);
}
export const activityHtml = (items) => (items.length
  ? items.map((x) => `<div class="pdx-act"><span class="pdx-dot" style="background:${TONE[x.tone] || TONE.todo};"></span><span class="pdx-act-text">${esc(x.text)}</span><span class="pdx-act-when">${esc(day(x.when))}</span></div>`).join('')
  : '<p class="g-note">Nothing on record yet.</p>');

// ── Document control (house numbering) ───────────────────────────────────────
/** ev = files.js evaluate() — { checked, fixes, matches }. */
export function docControl(ev) {
  if (!ev) return { tone: 'grey', verdict: 'Reading Drive…', headline: 'Checking the project folders against the house numbering.' };
  const checked = ev.checked.length; const fixes = ev.fixes.length;
  if (!checked) return { tone: 'grey', verdict: 'Nothing to check', headline: 'No PDFs, drawings or documents in the checked folders yet.' };
  return {
    tone: fixes ? 'attn' : 'ok',
    verdict: fixes ? `${fixes} to fix` : 'Matches',
    headline: `${checked - fixes} of ${checked} issued files follow the house numbering.`,
  };
}
export function docControlHtml(dc, { number = '', open = false } = {}) {
  const tone = dc.tone === 'attn' ? TONE.attn : dc.tone === 'ok' ? TONE.ok : TONE.todo;
  return `<div class="pdx-dc" id="pdx-dc-row" role="button" tabindex="0" aria-expanded="${open}" style="border-color:${dc.tone === 'attn' ? TONE.attn : 'var(--hair)'};">
      <div class="pdx-dc-head"><span class="pdx-dot" style="background:${tone};"></span><span class="pdx-eyebrow">Before issue</span><span class="pdx-flex"></span>
        <span class="pdx-dc-verdict" style="color:${dc.tone === 'grey' ? 'var(--ink-3)' : tone};">${esc(dc.verdict)}</span><span class="pdx-chev">${open ? '▴' : '▾'}</span></div>
      <div class="pdx-dc-line">${esc(dc.headline)}</div>
      <div class="pdx-dc-conv">${esc(number || 'PROJECT')}-04-0101 · then _A, _B on revision</div>
    </div>`;
}
/** The expanded list: what to rename, with the right name beside it — or,
 *  when Track can't be sure of the whole name, what is missing. Only issued
 *  file types are checked (PDF, DWG/DXF, Word, Excel). */
export function docFixesHtml(ev, { max = 8 } = {}) {
  if (!ev) return '';
  const rows = ev.fixes.slice(0, max).map((c) => {
    const sug = c.result.suggestion;
    const to = sug && sug.name ? ` → ${esc(sug.name)}` : '';
    const note = sug && sug.note ? `<span class="pdx-fix-why">${esc(sug.note)}</span>` : '';
    return `<div class="pdx-fix"><span class="pdx-dot" style="background:var(--brand-orange);"></span><div class="pdx-fix-body">
      <span class="pdx-fix-name">${esc(c.name)}</span>
      <span class="pdx-fix-why">${esc(c.result.reasons[0] || 'Does not match')}${to}</span>${note}</div></div>`;
  }).join('');
  const more = ev.fixes.length - Math.min(max, ev.fixes.length);
  return `<div class="pdx-eyebrow">Issued files checked — ${ev.checked.length}</div>${rows || '<p class="g-note">Every checked file matches.</p>'}
    ${more > 0 ? `<button type="button" class="pdx-linkbtn" data-dc-all>Show all ${ev.fixes.length}</button>` : ''}
    <span class="pdx-note">Rename in Drive; this updates within a minute.</span>`;
}

// ── Linked records ───────────────────────────────────────────────────────────
export const linkedHtml = (rows) => rows.map((r) => `<div class="pdx-link${r.id ? ' is-link' : ''}"${r.id ? ` id="${esc(r.id)}" role="button" tabindex="0"` : ''}>
    <span class="pdx-link-kind">${esc(r.kind)}</span>
    <span class="pdx-link-val" style="color:${r.tone === 'attn' ? TONE.attn : r.id ? TONE.link : 'var(--ink)'};">${r.html != null ? r.html : esc(r.value)}</span>
    ${r.meta ? `<span class="pdx-link-meta">${esc(r.meta)}</span>` : ''}
  </div>`).join('');

// ── Files: the project's real folders ────────────────────────────────────────
/** job = shapeProject() for this project; ev = evaluate(). */
export function folderRows(job, ev) {
  if (!job || !job.folders) return [];
  const byId = new Map((ev ? ev.folders : []).map((f) => [f.fo.id, f]));
  return job.folders.map((fo) => {
    const e = byId.get(fo.id);
    const count = e ? e.count : fo.files.length;
    return {
      id: fo.id, name: fo.name, count, webViewLink: fo.webViewLink || '',
      updated: e && e.latest ? day(e.latest.modifiedTime) : '—',
      dc: e ? e.dc : { label: '—', tone: 'grey' },
    };
  });
}
export const filesListHtml = (rows) => (rows.length ? rows.map((f) => `<div class="pdx-file" role="button" tabindex="0" data-folder="${esc(f.id)}">
    <span class="pdx-file-name" style="color:${f.count ? 'var(--ink)' : 'var(--ink-3)'};">${esc(f.name)}</span>
    <span class="pdx-file-n">${f.count ? `${f.count} file${f.count === 1 ? '' : 's'}` : 'empty'}</span></div>`).join('')
  : '<p class="g-note">No folders found in this project’s Drive folder.</p>');
export const folderTableHtml = (rows) => `<div class="pdx-ftable">
    <div class="pdx-frow pdx-fhead"><span>Folder</span><span>Files</span><span>Updated</span><span>Doc control</span></div>
    ${rows.map((f) => `<div class="pdx-frow" role="button" tabindex="0" data-folder="${esc(f.id)}">
      <span class="pdx-fname">${esc(f.name)}</span>
      <span style="color:${f.count ? 'var(--ink)' : 'var(--ink-3)'};">${f.count || 'empty'}</span>
      <span class="pdx-fupd">${esc(f.updated)}</span>
      <span class="pdx-fdc" style="color:${f.dc.tone === 'attn' ? TONE.attn : f.dc.tone === 'ok' ? 'var(--ink)' : 'var(--ink-3)'};"><span class="pdx-dot" style="background:${f.dc.tone === 'attn' ? TONE.attn : f.dc.tone === 'ok' ? TONE.ok : TONE.todo};"></span>${esc(f.dc.label)}</span>
    </div>`).join('')}
  </div>`;

// ── Site constraints (design hand-off "Site checks", 15/10/2026) ─────────────
// Built exactly like Document control: a collapsed .pdx-dc row and a
// .pdx-dc-open panel under it. The snapshot is hostile third-party data, so
// every value goes through esc(); the shared shapes (markers, badges, verdict,
// headline) come from site-checks-ui.js so no surface can drift.
const SEV_COLOUR = { warning: 'var(--status-error)', action: 'var(--brand-orange)' };
const SEV_INK = { warning: 'var(--status-error)', action: 'var(--cx-attn-ink, var(--brand-orange))' };
const SC_SPINNER = '<span class="scb-spin" aria-hidden="true"></span>';

/** Initials for a note's author: a name ("Ahmed Hussain" → AH) or an email (ahmed.hussain@… → AH). */
export function noteInitials(by) {
  const s = String(by || '').trim();
  if (!s) return '?';
  const base = s.includes('@') ? s.split('@')[0].replace(/[._-]+/g, ' ') : s;
  return base.split(/\s+/).map((w) => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase() || '?';
}

/**
 * The collapsed row. state: { open, running, runningWhy ('pin'|'manual'),
 * located, canRerun, now }. snap may be null (not run / first run).
 */
export function siteCheckHtml(snap, state = {}) {
  const { open = false, running = false, runningWhy = 'manual', located = true, canRerun = false, now = Date.now() } = state;
  // 6 · No address — or an address with no check on record yet.
  if (!snap && !running) {
    const line = located ? 'No check on record yet for this site.' : 'Add a site address or drop a pin to run checks.';
    const pills = located
      ? `${canRerun ? '<button type="button" class="pdx-pill is-primary" data-sc="rerun">Run checks</button>' : ''}<button type="button" class="pdx-pill" data-sc="pin">Drop a pin</button>`
      : '<button type="button" class="pdx-pill" data-sc="add-address">Add address</button><button type="button" class="pdx-pill is-primary" data-sc="pin">Drop a pin</button>';
    return `<div class="pdx-dc scb-block is-static" id="pdx-sc-row" style="border-color:var(--hair);">
      <div class="pdx-dc-head">${severityMark('info', { size: 12, label: false })}<span class="pdx-eyebrow">Site constraints</span><span class="pdx-flex"></span>
        <span class="pdx-dc-verdict" style="color:var(--ink-3);" aria-live="polite">Not run</span></div>
      <div class="pdx-dc-line">${esc(line)}</div>
      <div class="scb-pills">${pills}</div>
    </div>`;
  }
  // 7 · Running — the previous line dimmed, or a shimmer bar on a first run.
  if (running) {
    const conv = runningWhy === 'pin' ? 'Pin moved · previous results dimmed until the new ones land'
      : snap ? 'Re-running · previous results dimmed until the new ones land' : 'Checking five public datasets — usually a few seconds';
    return `<div class="pdx-dc scb-block is-static" id="pdx-sc-row" aria-busy="true" style="border-color:var(--hair);">
      <div class="pdx-dc-head">${SC_SPINNER}<span class="pdx-eyebrow">Site constraints</span><span class="pdx-flex"></span>
        <span class="pdx-dc-verdict" style="color:var(--link);" aria-live="polite">Re-checking…</span></div>
      ${snap ? `<div class="pdx-dc-line scb-dim">${esc(headlineText(snap))}</div>` : '<div class="pdx-dc-line"><span class="fm-skel scb-sk" aria-hidden="true"></span></div>'}
      <div class="pdx-dc-conv">${esc(conv)}</div>
    </div>`;
  }
  const worst = worstSeverity(snap);
  const tier = tierOf(snap);
  const covered = SOURCE_ORDER.length - SOURCE_ORDER.filter((k) => snap.results?.[k]?.status === 'not-covered').length;
  let conv;
  // No "stale" state (Karan 16/10/2026): a check is never marked old — the
  // line always carries the absolute date it ran, and Re-run is always to hand.
  if (tier === 'postcode') {
    // 8 · Approximate: the badge itself, then "Drop a pin".
    conv = `<div class="pdx-dc-conv scb-conv-row">Checked ${esc(scDate(snap.createdAt))} · ${locationBadge('postcode')}<button type="button" class="pdx-linkbtn scb-inline" data-sc="pin">Drop a pin</button>${covered < SOURCE_ORDER.length ? `<span>· ${covered} of ${SOURCE_ORDER.length} sources</span>` : ''}</div>`;
  } else {
    conv = `<div class="pdx-dc-conv">${esc(checkedLine(snap))}</div>`;
  }
  return `<div class="pdx-dc scb-block" id="pdx-sc-row" style="border-color:${SEV_COLOUR[worst] || 'var(--hair)'};">
      <button type="button" class="pdx-dc-head scb-toggle" data-sc="toggle" aria-expanded="${open}" aria-controls="pdx-sc-open">${severityMark(worst === 'none' ? 'info' : worst, { size: 12 })}<span class="pdx-eyebrow">Site constraints</span><span class="pdx-flex"></span>
        <span class="pdx-dc-verdict" style="color:${SEV_INK[worst] || 'var(--ink-3)'};" aria-live="polite">${esc(verdictText(snap))}</span><span class="pdx-chev scb-chev" aria-hidden="true">▾</span></button>
      <div class="pdx-dc-line">${esc(headlineText(snap))}</div>
      ${conv}
    </div>`;
}

/** What happened on a flag, under its row: the task, the fee note, the notes with initials. */
function flagOutcomes(f, done = {}) {
  const out = [];
  if (done.task != null || f.taskRef) out.push(`<span class="scb-done">✓ Task created${done.task ? ` · ${esc(done.task)}` : ''}</span>`);
  if (f.feeNoteAdded) out.push('<span class="scb-done">✓ Added to fee proposal notes</span>');
  for (const n of Array.isArray(f.notes) ? f.notes : []) {
    out.push(`<span class="scb-fnote"><b>${esc(noteInitials(n.byName || n.by))}</b> · “${esc(n.text)}”</span>`);
  }
  return out.join('');
}

/**
 * The open panel: one row per flag (warning → action → info), the failed
 * sources as Info rows with Retry, the five chips, the links and the note.
 * state: { noteFor, done: { [flagId]: { task } }, canRerun, canAnnotate, busyFlag }.
 */
export function siteFlagsHtml(snap, state = {}) {
  if (!snap) return '';
  const { noteFor = null, done = {}, canRerun = false, canAnnotate = true, busyFlag = null } = state;
  const tier = tierOf(snap);
  const failed = failedSources(snap);
  const flags = sortedFlags(snap);
  const rows = [];
  for (const f of flags) {
    const id = String(f.id || '');
    const src = flagSource(f);
    const down = /^check-incomplete/.test(id);
    const approx = id === 'approximate-location';
    const title = down && src ? `${SOURCE_LABEL[src]} data unavailable` : f.title;
    const why = down ? `${f.action || 'The service did not respond.'} Don’t read this as clear.` : f.action;
    let tail;
    if (down) tail = canRerun ? '<button type="button" class="pdx-linkbtn scb-retry" data-sc="rerun">Retry</button>' : '';
    else if (approx) tail = '<button type="button" class="pdx-linkbtn scb-retry" data-sc="pin">Drop a pin</button>';
    else if (id === 'no-location' || !canAnnotate) tail = '';
    else tail = `<button type="button" class="scb-more" data-sc="menu" data-flag="${esc(id)}" aria-label="Flag options" aria-haspopup="menu">⋯</button>`;
    const after = flagOutcomes(f, done[id]);
    const form = noteFor === id && canAnnotate ? `<div class="scb-note-form">
        <input type="text" class="scb-note-in" data-flag="${esc(id)}" maxlength="500" aria-label="Note on this flag" placeholder="e.g. Ash tree about 6 m from the rear wall"${busyFlag === id ? ' disabled' : ''}>
        <button type="button" class="pdx-pill is-primary" data-sc="note-save" data-flag="${esc(id)}"${busyFlag === id ? ' disabled' : ''}>${busyFlag === id ? 'Saving…' : 'Add note'}</button>
        <button type="button" class="pdx-linkbtn" data-sc="note-cancel">Cancel</button></div>` : '';
    rows.push(`<div class="pdx-fix scb-flag" data-flag-row="${esc(id)}">
      ${severityMark(f.severity, { size: 12 })}
      <div class="scb-flag-main">
        <button type="button" class="pdx-fix-body scb-flag-open" data-sc="source" data-site-source="${esc(src)}"${src ? '' : ' disabled'}>
          <span class="pdx-fix-name">${esc(title || '—')}</span>
          ${why ? `<span class="pdx-fix-why scb-why">${esc(why)}</span>` : ''}
        </button>
        ${after || form ? `<div class="scb-after">${after}${form}</div>` : ''}
      </div>
      ${tail}
    </div>`);
  }
  // A failed source the server did not flag still reads as a row, never as clear.
  for (const k of failed) {
    if (flags.some((f) => flagSource(f) === k && /^check-incomplete/.test(String(f.id || '')))) continue;
    rows.push(`<div class="pdx-fix scb-flag">${severityMark('info', { size: 12 })}<div class="scb-flag-main"><div class="pdx-fix-body"><span class="pdx-fix-name">${esc(SOURCE_LABEL[k])} data unavailable</span><span class="pdx-fix-why scb-why">The service did not respond. Don’t read this as clear.</span></div></div>${canRerun ? '<button type="button" class="pdx-linkbtn scb-retry" data-sc="rerun">Retry</button>' : ''}</div>`);
  }
  // 8 · Approximate: the Info row says so even on a snapshot written before the server flagged it.
  if (tier === 'postcode' && !flags.some((f) => f.id === 'approximate-location')) {
    rows.push(`<div class="pdx-fix scb-flag">${severityMark('info', { size: 12 })}<div class="scb-flag-main"><div class="pdx-fix-body"><span class="pdx-fix-name">Location is approximate</span><span class="pdx-fix-why scb-why">Checked at the postcode centre. Drop a pin on the plot for an accurate check.</span></div></div><button type="button" class="pdx-linkbtn scb-retry" data-sc="pin">Drop a pin</button></div>`);
  }
  return `${rows.join('') || '<p class="g-note">No flags raised by the sources checked.</p>'}
    <div class="scb-chips">${SOURCE_ORDER.map((k) => sourceChip(k, snap)).join('')}</div>
    <div class="scb-foot">
      <button type="button" class="pdx-linkbtn" data-sc="drawer">All sources and values →</button>
      ${canRerun ? '<button type="button" class="pdx-linkbtn" data-sc="rerun">Re-run</button>' : ''}
      <span class="pdx-note scb-foot-note">${esc(SCREENING_NOTE)}</span>
    </div>`;
}

/** The masthead's worst flag — ONLY when there is a Warning; '' for clean or Action-only sites. */
export function siteMastHtml(snap) {
  const f = sortedFlags(snap)[0];
  if (!f || f.severity !== 'warning') return '';
  return `<span class="pdx-sep">·</span><a href="#pdx-sc" class="scb-mast" data-sc="mast">${severityMark('warning', { size: 11 })}${esc(cap1(shortFlagName(f)))}</a>`;
}
const cap1 = (s) => (s ? s[0].toUpperCase() + s.slice(1) : s);

/** The location badge + pin link for the Linked records Site row. */
export function siteRowExtraHtml(snap, project = {}) {
  const tier = snap ? tierOf(snap) : (project.siteLat != null && project.siteLng != null ? 'address' : 'postcode');
  // Google's address match covers almost every site (Karan 16/10/2026): only a
  // postcode-level check asks for a pin. An address-level site can still be
  // pinned by clicking the address (the pin overlay) or from the panel.
  const link = tier === 'pin' ? 'Move pin' : tier === 'postcode' ? 'Drop a pin' : '';
  return `${locationBadge(tier)}${link ? `<button type="button" class="pdx-linkbtn scb-inline" data-sc="pin">${link}</button>` : ''}`;
}

/** One activity item when the latest re-run CHANGED a result; null otherwise. */
export function siteActivityItem(snap) {
  const text = diffText(snap);
  if (!snap || !text) return null;
  const d = new Date(snap.createdAt);
  if (isNaN(d)) return null;
  return { when: d, text: `${triggerPhrase(snap.trigger)} — ${text}`, tone: 'attn' };
}

// ── Finance tab: the fee on each instruction, and why when it is under the
// calculation (Add instruction redesign, decision 3, Karan 26/10/2026). A fee
// below what the shared calculator worked out carries the reason it was given.
const gbp = (n) => `£${Math.round(Number(n) || 0).toLocaleString('en-GB')}`;
export function instructionFeesHtml(instructions = []) {
  const rows = instructions.slice().sort((a, b) => (Number(a.seq) || 0) - (Number(b.seq) || 0))
    .filter((i) => Number(i.fee) > 0 || i.unpriced || i.calcFee != null || i.feeReason)
    .map((i) => {
      const fee = Number(i.fee) || 0;
      const calc = i.calcFee != null ? Number(i.calcFee) : null;
      const gap = calc != null ? fee - calc : 0;
      const sub = i.unpriced && !fee ? 'Unpriced — a task is open to price it'
        : calc != null && gap !== 0 ? `${gbp(Math.abs(gap))} ${gap < 0 ? 'below' : 'above'} the calculation (${gbp(calc)})${i.feeReason ? ` — ${i.feeReason}` : ''}`
        : calc != null ? 'As calculated' : (i.feeReason || '');
      return `<div class="pd-ifee" role="row">
        <span class="pd4-it-n">I${esc(i.seq)}</span>
        <span class="pd-ifee-d"><span>${esc(i.description || 'Instruction')}</span>${sub ? `<span class="pd-ifee-s${gap < 0 ? ' is-below' : ''}">${esc(sub)}</span>` : ''}</span>
        <span class="pd-ifee-f">${fee ? esc(gbp(fee)) : '—'}</span>
      </div>`;
    });
  if (!rows.length) return '';
  return `<div class="pd-ifees"><div class="pd4-sec-h"><h2>Fees by instruction</h2></div>${rows.join('')}</div>`;
}
